import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Typography, Tag, Select, Button, Space, Row, Col, Statistic,
  Descriptions, Input, Modal, Popconfirm, EmptyState, Drawer, message,
} from '../../ui';
import {
  Inbox, UserPlus, Phone, CheckCircle2, Ban,
  Redo2, MapPin, Zap,
} from 'lucide-react';
import { request } from '../../api/client';
import { usePermission, useAuthStore } from '../../store/auth';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { formatIst } from '../../lib/time';
import { brand } from '../../assets/brand';

const STATUS_COLOR: Record<string, string> = { NEW: 'blue', ASSIGNED: 'gold', CONTACTED: 'cyan', CONVERTED: 'green', CLOSED: 'default' };
const TERMINAL = ['CONVERTED', 'CLOSED'];
const fmtDist = (m: number | null | undefined) => (m == null ? '' : m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`);

export function InquiriesPage() {
  const qc = useQueryClient();
  const canRespond = usePermission('INQUIRY.RESPOND');
  const canAssign = usePermission('INQUIRY.ASSIGN');
  const canConvert = usePermission('INQUIRY.CONVERT');
  // Reopen is admin-only (the service enforces this); gate the button by role so a Sales
  // Head — who also holds INQUIRY.ASSIGN — isn't shown an action that would 403.
  const role = useAuthStore((s) => s.user?.role);
  const canReopen = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const [status, setStatus] = useState<string | undefined>();
  const [openId, setOpenId] = useState<string | null>(null);
  const [assignFor, setAssignFor] = useState<any | null>(null);
  const [assignTo, setAssignTo] = useState<string | undefined>();

  const { data, isLoading } = useQuery({
    queryKey: ['inquiries', status],
    queryFn: () => request<any[]>({ url: '/inquiries', params: { status } }),
  });
  const rows = data ?? [];

  const detailQ = useQuery({
    queryKey: ['inquiry', openId],
    queryFn: () => request<any>({ url: `/inquiries/${openId}` }),
    enabled: !!openId,
  });
  const sel = detailQ.data;

  // Sales persons for the assign picker (best-effort; backend still enforces SH→reports).
  const { data: users } = useQuery({
    queryKey: ['inquiry-assignable-users'],
    queryFn: () => request<any>({ url: '/users', params: { pageSize: 200 } }),
    enabled: !!assignFor,
  });
  const spOptions = useMemo(
    () => (users?.data ?? [])
      .filter((u: any) => (u.role?.code ?? u.roleCode) === 'SALES_PERSON')
      .map((u: any) => ({ value: String(u.id), label: `${u.fullName}${u.mobile ? ` · ${u.mobile}` : ''}` })),
    [users],
  );

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['inquiries'] });
    qc.invalidateQueries({ queryKey: ['inquiry'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };
  const action = useMutation({
    mutationFn: ({ id, path, body }: { id: string; path: string; body?: any }) =>
      request({ url: `/inquiries/${id}/${path}`, method: 'PATCH', data: body ?? {} }),
    onSuccess: () => { message.success('Inquiry updated'); refresh(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const kpis = useMemo(() => {
    let nw = 0, active = 0, converted = 0;
    for (const r of rows) {
      if (r.status === 'NEW') nw++;
      if (['NEW', 'ASSIGNED', 'CONTACTED'].includes(r.status)) active++;
      if (r.status === 'CONVERTED') converted++;
    }
    return { nw, active, converted, total: rows.length };
  }, [rows]);

  const askNotes = (title: string, onOk: (notes: string) => void, required = false) => {
    let v = '';
    Modal.confirm({
      title, width: 460, okText: 'Save',
      content: <Input.TextArea rows={3} onChange={(e) => (v = e.target.value)} placeholder={required ? 'Required…' : 'Optional notes…'} />,
      onOk: () => { if (required && !v.trim()) { message.error('A note is required'); return Promise.reject(); } onOk(v.trim()); },
    });
  };

  const columns: DataTableColumn<any>[] = [
    { key: 'inquiryNo', title: 'Inquiry', dataIndex: 'inquiryNo', width: 130, alwaysVisible: true },
    {
      key: 'customer', title: 'Customer', alwaysVisible: true,
      render: (_: any, r: any) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.customerName}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{[r.customerWhatsapp || r.customerPhone, r.customerEmail].filter(Boolean).join(' · ')}</div>
        </div>
      ),
    },
    { key: 'product', title: 'Product', width: 180, render: (_: any, r: any) => r.productName ?? '—' },
    { key: 'retailer', title: 'Dealer', width: 170, render: (_: any, r: any) => r.chosenRetailerName ?? <span style={{ color: '#bbb' }}>— (unrouted)</span> },
    { key: 'sp', title: 'Sales person', width: 150, render: (_: any, r: any) => r.assignedToName ?? r.chosenSalesPersonName ?? <span style={{ color: '#bbb' }}>—</span> },
    { key: 'status', title: 'Status', dataIndex: 'status', width: 120, render: (v: string) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
    { key: 'createdAt', title: 'Received', dataIndex: 'createdAt', width: 160, sortable: true, render: (v: any) => formatIst(v) },
  ];

  return (
    <div>
      <Typography.Title level={4}>Product Inquiries</Typography.Title>
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card><Statistic title="New (triage)" value={kpis.nw} prefix={<Zap size={16} />} valueStyle={{ color: brand.primary }} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Active" value={kpis.active} prefix={<Inbox size={16} />} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Converted" value={kpis.converted} prefix={<CheckCircle2 size={16} />} valueStyle={{ color: '#52C41A' }} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Total" value={kpis.total} /></Card></Col>
      </Row>

      <Card>
        <DataTable
          storageKey="inquiries-list"
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          onRow={(r: any) => ({ onClick: () => setOpenId(String(r.id)), style: { cursor: 'pointer' } })}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          toolbarLeft={
            <Select placeholder="Status" allowClear style={{ width: 160 }} value={status} onChange={setStatus}
              options={Object.keys(STATUS_COLOR).map((s) => ({ value: s, label: s }))} />
          }
        />
      </Card>

      <Drawer
        open={!!openId}
        onClose={() => setOpenId(null)}
        width={560}
        title={sel ? `${sel.inquiryNo} · ${sel.customerName}` : 'Inquiry'}
      >
        {sel && (
          <>
            <Space wrap style={{ marginBottom: 14 }}>
              {canRespond && sel.status === 'NEW' && (
                <Button variant="primary" icon={<Zap size={16} />} loading={action.isPending}
                  onClick={() => action.mutate({ id: sel.id, path: 'claim' })}>Claim</Button>
              )}
              {canAssign && !TERMINAL.includes(sel.status) && (
                <Button variant="default" icon={<UserPlus size={16} />} onClick={() => { setAssignFor(sel); setAssignTo(undefined); }}>Assign</Button>
              )}
              {canRespond && (sel.status === 'ASSIGNED' || sel.status === 'CONTACTED') && (
                <Button variant="default" icon={<Phone size={16} />} onClick={() => askNotes('Mark contacted', (notes) => action.mutate({ id: sel.id, path: 'contact', body: { notes } }))}>Mark contacted</Button>
              )}
              {canConvert && (sel.status === 'ASSIGNED' || sel.status === 'CONTACTED') && (
                <Button variant="primary" icon={<CheckCircle2 size={16} />} onClick={() => askNotes('Convert to order', (notes) => action.mutate({ id: sel.id, path: 'convert', body: { notes } }))}>Convert</Button>
              )}
              {canRespond && !TERMINAL.includes(sel.status) && (
                <Button variant="danger" icon={<Ban size={16} />} onClick={() => askNotes('Close inquiry', (reason) => action.mutate({ id: sel.id, path: 'close', body: { reason } }), true)}>Close</Button>
              )}
              {canReopen && sel.status === 'CLOSED' && (
                <Popconfirm title="Reopen this inquiry?" onConfirm={() => action.mutate({ id: sel.id, path: 'reopen' })}>
                  <Button variant="default" icon={<Redo2 size={16} />}>Reopen</Button>
                </Popconfirm>
              )}
            </Space>

            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Status"><Tag color={STATUS_COLOR[sel.status]}>{sel.status}</Tag></Descriptions.Item>
              <Descriptions.Item label="Product">{sel.productName ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Customer">{sel.customerName}</Descriptions.Item>
              <Descriptions.Item label="WhatsApp / phone">
                {sel.customerWhatsapp || sel.customerPhone
                  ? <a href={`tel:${sel.customerWhatsapp || sel.customerPhone}`}>{sel.customerWhatsapp || sel.customerPhone}</a>
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">{sel.customerEmail ? <a href={`mailto:${sel.customerEmail}`}>{sel.customerEmail}</a> : '—'}</Descriptions.Item>
              <Descriptions.Item label="Message">{sel.message ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Dealer">{sel.chosenRetailerName ?? '— (unrouted, central triage)'}</Descriptions.Item>
              <Descriptions.Item label="Sales person">{sel.assignedToName ?? sel.chosenSalesPersonName ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Territory">{sel.territoryName ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Location">
                {sel.latitude && sel.longitude
                  ? <a href={`https://www.google.com/maps/search/?api=1&query=${sel.latitude},${sel.longitude}`} target="_blank" rel="noopener">
                      <MapPin size={16} /> {Number(sel.latitude).toFixed(4)}, {Number(sel.longitude).toFixed(4)}{sel.accuracyMeters ? ` (±${sel.accuracyMeters}m)` : ''}
                    </a>
                  : [sel.city, sel.pincode].filter(Boolean).join(' · ') || '—'}
                <span style={{ color: '#bbb', fontSize: 12 }}> · {sel.locationSource}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Received">{formatIst(sel.createdAt)}</Descriptions.Item>
              {sel.contactedAt && <Descriptions.Item label="Contacted">{formatIst(sel.contactedAt)}</Descriptions.Item>}
              {sel.convertedAt && <Descriptions.Item label="Converted">{formatIst(sel.convertedAt)}</Descriptions.Item>}
              {sel.closedReason && <Descriptions.Item label="Closed reason">{sel.closedReason}</Descriptions.Item>}
              {sel.staffNotes && <Descriptions.Item label="Staff notes"><span style={{ whiteSpace: 'pre-wrap' }}>{sel.staffNotes}</span></Descriptions.Item>}
            </Descriptions>
          </>
        )}
        {!detailQ.isLoading && !sel && <EmptyState title="Could not load inquiry" />}
      </Drawer>

      <Modal
        open={!!assignFor}
        onOpenChange={(o) => { if (!o) setAssignFor(null); }}
        title="Assign inquiry to a sales person"
        footer={<>
          <Button variant="default" onClick={() => setAssignFor(null)}>Cancel</Button>
          <Button variant="primary" onClick={() => {
            if (!assignTo) { message.error('Pick a sales person'); return; }
            action.mutate({ id: assignFor.id, path: 'assign', body: { assignedToUserId: assignTo } }, { onSuccess: () => setAssignFor(null) });
          }}>Assign</Button>
        </>}
      >
        <Select showSearch optionFilterProp="label" style={{ width: '100%' }} placeholder="Select sales person"
          value={assignTo} onChange={setAssignTo} options={spOptions} />
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
          A Sales Head can only assign to a sales person who reports to them.
        </Typography.Paragraph>
      </Modal>
    </div>
  );
}
